/* eslint-disable id-length */
import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Menu, MenuItem } from '@mui/material';
import DownloadIcon from '../../../Assets/images/svg/dar_collection_dashboard_download.svg';
import MoreIcon from '../../../Assets/images/svg/dar_more_icon.svg';

const ExpandableSection = ({
  sectionId,
  title,
  expanded,
  onToggle,
  children,
  headerActionLabel,
  onHeaderAction,
  onDownload,
  onHelp,
  onMore,
  showDownloadAction,
  showMoreAction,
  onMoreMenuItems,
  isLoading,
}) => {
  const contentRef = useRef(null);
  const [moreAnchorEl, setMoreAnchorEl] = useState(null);


  const runDownloadAction = async (event) => {
    // Prevent the accordion from opening/closing when you click the download button
    event.stopPropagation();

    if (typeof onDownload === 'function') {
      // Force the parent component to handle the download using raw state data
      await onDownload({ format: 'excel', title, contentElement: contentRef.current });
    } else {
      console.warn(`No onDownload handler provided for section: ${title}`);
    }
  };

  const handleMoreIconClick = (event) => {
    event.stopPropagation();
    if (!onMoreMenuItems.length) {
      onMore?.();
      return;
    }
    setMoreAnchorEl(event.currentTarget);
  };

  const handleCloseMoreMenu = () => {
    setMoreAnchorEl(null);
  };

  const handleMoreMenuItemClick = async (menuItem) => {
    if (menuItem?.disabled) return;
    if (typeof menuItem?.onClick === 'function') {
      await menuItem.onClick({ title, contentElement: contentRef.current });
    } else if (typeof onMore === 'function') {
      await onMore(menuItem);
    }
    handleCloseMoreMenu();
  };

  return (
    <div id={sectionId || undefined} className="expandable-item">
      <div className="expand-header">
        <button
          type="button"
          className="expand-toggle"
          aria-expanded={expanded}
          onClick={onToggle}
        >
          <span className="expand-title">{title}</span>
        </button>
        <span className="expand-actions">
          {headerActionLabel && onHeaderAction ? (
            <button
              type="button"
              className="expand-header-link"
              onClick={(e) => {
                e.stopPropagation();
                onHeaderAction();
              }}
            >
              {headerActionLabel}
            </button>
          ) : null}
          {showDownloadAction ? (
            <button
              type="button"
              className={`expand-action ${!expanded ? 'disabled-action' : ''}`}
              aria-label="Download"
              onClick={runDownloadAction} // This now strictly relies on the parent's data
              disabled={!expanded}
            >
              <img src={DownloadIcon} alt="" />
            </button>
          ) : null}
          <button
            type="button"
            className="expand-action"
            aria-label="Help"
            onClick={(e) => {
              e.stopPropagation();
              onHelp?.();
            }}
          >
            ?
          </button>
          {showMoreAction ? (
            <button
              type="button"
              className="expand-action"
              aria-label="More options"
              onClick={handleMoreIconClick}
            >
              <img src={MoreIcon} alt="" />
            </button>
          ) : null}
          <button
            type="button"
            className="expand-icon-btn"
            aria-label={expanded ? 'Collapse section' : 'Expand section'}
            onClick={onToggle}
          >
            <svg
              className="expand-icon"
              stroke="currentColor"
              fill="currentColor"
              strokeWidth="0"
              viewBox="0 0 448 512"
              height="1em"
              width="1em"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                d={
                  'M207.029 381.476L12.686 187.132c-9.373-9.373-9.373-24.569 0-33.941 '
                  + 'l22.667-22.667c9.357-9.357 24.522-9.375 33.901-.04L224 284.505 '
                  + 'l154.745-154.021c9.379-9.335 24.544-9.317 33.901.04l22.667 22.667 '
                  + 'c9.373 9.373 9.373 24.569 0 33.941L240.971 381.476c-9.373 9.372 '
                  + '-24.569 9.372-33.942 0z'
                }
              />
            </svg>
          </button>
        </span>
      </div>
      {onMoreMenuItems.length > 0 && (
        <Menu
          anchorEl={moreAnchorEl}
          open={Boolean(moreAnchorEl)}
          onClose={handleCloseMoreMenu}
          onClick={(event) => event.stopPropagation()}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{ paper: { className: 'expand-more-menu-paper' } }}
        >
          {onMoreMenuItems.map((menuItem) => (
            <MenuItem
              key={menuItem.id || menuItem.label}
              className="expand-more-menu-item"
              disabled={Boolean(menuItem.disabled)}
              onClick={async () => handleMoreMenuItemClick(menuItem)}
            >
              {menuItem.label}
            </MenuItem>
          ))}
        </Menu>
      )}
      {expanded && children && (
        <div className="expandable-content" ref={contentRef}>
          {isLoading ? (
            <div
              className="section-loading-placeholder"
              style={{ padding: '24px', textAlign: 'center', color: '#888', fontStyle: 'italic' }}
            >
              Loading section details...
            </div>
          ) : (
            children
          )}
        </div>
      )}
    </div>
  );
};

ExpandableSection.propTypes = {
  sectionId: PropTypes.string,
  title: PropTypes.string.isRequired,
  expanded: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  children: PropTypes.node,
  headerActionLabel: PropTypes.string,
  onHeaderAction: PropTypes.func,
  onDownload: PropTypes.func,
  onHelp: PropTypes.func,
  onMore: PropTypes.func,
  showDownloadAction: PropTypes.bool,
  showMoreAction: PropTypes.bool,
  onMoreMenuItems: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    label: PropTypes.string.isRequired,
    onClick: PropTypes.func,
    disabled: PropTypes.bool,
  })),
  isLoading: PropTypes.bool,
};

ExpandableSection.defaultProps = {
  sectionId: '',
  children: null,
  headerActionLabel: '',
  onHeaderAction: null,
  onDownload: null,
  onHelp: null,
  onMore: null,
  showDownloadAction: true,
  showMoreAction: true,
  onMoreMenuItems: [],
  isLoading: false,
};

export default ExpandableSection;
